import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

interface HOAInfo {
  id: string;
  name: string;
  slug: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

const HOALogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [hoaInfo, setHoaInfo] = useState<HOAInfo | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { hoaSlug } = useParams<{ hoaSlug: string }>();

  useEffect(() => {
    const fetchHOAInfo = async () => {
      if (!hoaSlug) {
        setError('Invalid property identifier');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get<HOAInfo>(`/api/hoas/slug/${hoaSlug}`);
        setHoaInfo(response.data);
        setLoading(false);
      } catch (err: any) {
        console.error('Failed to fetch property info:', err);
        if (err.response?.status === 404) {
          setError('Property not found. Please check the URL.');
        } else {
          setError('Failed to load property information');
        }
        setLoading(false);
      }
    };

    fetchHOAInfo();
  }, [hoaSlug]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!hoaInfo) {
      setError('Property information not available');
      return;
    }
    
    try {
      const userData = await login(email, password, hoaInfo.id);
      
      // Check if user belongs to this property
      if (userData?.hoaId !== hoaInfo.id) {
        setError('User does not belong to this property');
        return;
      }
      
      // Navigate based on user role
      if (userData?.role === 'ADMIN' && !userData?.hoaId) {
        navigate('/select-hoa');
      } else if (userData?.role === 'VENDOR') {
        navigate('/vendor-dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      
      if (err.response) {
        setError(err.response.data.message || 'Login failed');
      } else if (err.request) {
        setError('No response from server. Please check your network connection.');
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center">
        <div className="text-white">Loading property information...</div>
      </div>
    );
  }

  if (!hoaInfo) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center p-4">
        <div className="glass-card p-8 w-full max-w-md text-center">
          <div className="text-red-400 mb-4">⚠️ Property Not Found</div>
          <p className="text-white/60 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="btn-secondary"
          >
            View Property Directory
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#22d3ee]/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#38bdf8]/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-[#22d3ee]/5 to-[#38bdf8]/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 glass-card p-8 w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-[#22d3ee] to-[#38bdf8] flex items-center justify-center shadow-lg shadow-[#22d3ee]/25">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0v-4a2 2 0 012-2h2m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v8.1M9 21h4" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2 bg-gradient-to-r from-white to-white/80 bg-clip-text">
            {hoaInfo.name}
          </h1>
          <p className="text-white/60 text-sm">
            {hoaInfo.city}, {hoaInfo.state}
          </p>
          <p className="text-white/40 text-xs mt-1">
            Resident & Vendor Portal
          </p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl mb-6 backdrop-blur-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-white/80">
              Email or Username
            </label>
            <input
              type="text"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field w-full"
              placeholder="Enter your email or username"
              required
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium text-white/80">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field w-full"
              placeholder="Enter your password"
              required
            />
          </div>

          <button
            type="submit"
            className="btn-primary w-full"
          >
            Sign In to {hoaInfo.name}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-white/40 text-xs mb-2">
            Need access to a different property?
          </p>
          <button
            onClick={() => navigate('/')}
            className="text-[#22d3ee] hover:text-[#22d3ee]/80 text-sm underline"
          >
            View Property Directory
          </button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-white/40 text-xs">
            Secure • Modern • Efficient
          </p>
        </div>

        {/* Back to main site */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white text-sm transition duration-200"
          >
            ← Back to main site
          </button>
        </div>
      </div>
    </div>
  );
};

export default HOALogin; 